// ============================================================================
// Media Service
// Slot-addressed site media: list, limits, upload, delete.
// ============================================================================

import { SiteMedia, MediaLimits, MediaType } from '../types/site';
import { fetchWithAuth, fetchMultipartWithAuth } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

async function getStoreId(): Promise<number | undefined> {
  const { authService } = await import('./auth');
  return authService.getStoreId();
}

// ============================================================================
// Site Media CRUD
// ============================================================================

/**
 * Fetch one site's media rows. Server-side filter, not a whole-store fetch —
 * a site holds up to 11 rows now, so a large store would silently truncate at
 * the endpoint's default limit.
 *
 * Rows include `url` with a `?v=` cache-buster; render it verbatim.
 */
export async function getSiteMedia(siteAlias: string): Promise<SiteMedia[]> {
  const storeId = await getStoreId();
  const params = new URLSearchParams({ site_alias: siteAlias });
  if (storeId) params.set('store_id', String(storeId));
  return fetchWithAuth(`${API_BASE_URL}/medias/?${params.toString()}`);
}

/**
 * Business rules from the server: slot counts, converted-size caps, duration
 * limit, description length. Never hardcode these — the caller decides how
 * long to cache (see SiteMediaForm's TTL and re-fetch-per-batch policy).
 */
export async function getMediaLimits(): Promise<MediaLimits> {
  const storeId = await getStoreId();
  const params = storeId ? `?store_id=${storeId}` : '';
  const res = await fetchWithAuth(`${API_BASE_URL}/medias/limits${params}`);
  return res.limits;
}

export async function deleteSiteMedia(mediaId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/medias/${mediaId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// File Upload
// ============================================================================

/**
 * Upload a converted media blob into a specific slot. Uploading into an
 * occupied slot replaces it (server upserts on store/site/type/slot).
 *
 * `description` is appended only when non-empty: the server keeps an existing
 * description when the field is omitted, so a replace must not send an empty
 * string and wipe text the admin wrote earlier.
 *
 * Goes through fetchMultipartWithAuth so an upload after a multi-minute video
 * conversion still refreshes an expired token instead of failing.
 */
export async function uploadSiteMedia(
  blob: Blob,
  filename: string,
  siteAlias: string,
  mediaType: MediaType,
  displayOrder: number,
  description?: string
): Promise<SiteMedia> {
  const storeId = await getStoreId();
  const storeParam = storeId ? `?store_id=${storeId}` : '';

  const formData = new FormData();
  formData.append('file', blob, filename);
  formData.append('site_alias', siteAlias);
  formData.append('media_type', mediaType);
  formData.append('display_order', String(displayOrder));
  if (description && description.trim()) {
    formData.append('description', description.trim());
  }

  return fetchMultipartWithAuth(`${API_BASE_URL}/medias/upload${storeParam}`, formData);
}

// ============================================================================
// Named Export Object
// ============================================================================

export const mediaService = {
  getSiteMedia,
  getMediaLimits,
  deleteSiteMedia,
  uploadSiteMedia,
};

export default mediaService;
